import React, { useState, useEffect, useCallback } from 'react';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import {
  TemplateCreationData,
  TemplateSectionCreationData,
  SectionSplittingLevel,
} from 'src/company/templates/types';
import { SectionEditorLayout } from 'src/company/templates/components/SectionEditorLayout';
import { SectionContentEditorPane } from 'src/company/templates/components/SectionContentEditorPane';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useCreateTemplateStream } from 'src/company/templates/hooks/useCreateTemplate';
import useSplitMarkdownMutation from 'src/company/templates/hooks/useSplitMarkdownMutation';
import { v4 as uuidv4 } from 'uuid';
import { mergeSectionsIntoOne } from 'src/company/templates/components/template-creation-flow/steps/SplitMarkdownEditor';
import { IconBug } from '@tabler/icons-react';
import { Alert, List, Text, Loader, Group } from '@mantine/core';
import { useClients } from 'src/clients/hooks/useClients';

export default function EditSectionContentStep(props: StepProps<TemplateCreationData>) {
  const { data, setData } = props;
  const [selectedSectionId, setSelectedSectionId] = useState<string | undefined>(undefined);
  const { splitMarkdown } = useSplitMarkdownMutation();
  const { clients } = useClients({ isTest: true, companyId: data.company.id });

  useEffect(() => {
    setData({
      ...data,
      previewClientId: clients?.[0]?.id || null,
    });
  }, [clients]);

  useEffect(() => {
    splitMarkdownAndSave(data.sectionSplittingLevel).catch(error =>
      notifyAndLogError(
        'Failed to split markdown',
        'An error occurred while splitting the markdown. Please try again.',
        error,
      ),
    );
  }, [data.sectionSplittingLevel]);

  const splitMarkdownAndSave = async (level: number) => {
    const templateSections = await splitMarkdown({
      level: level,
      populatedMarkdown: data.examplePopulatedMarkdown || '',
      templateMarkdown: data.exampleTemplateMarkdown || '',
    });

    const templateSectionsWithId = templateSections.map(section => ({ ...section, id: uuidv4() }));
    setData({
      ...data,
      templateSections: templateSectionsWithId,
    });

    if (templateSectionsWithId.length > 0) {
      setSelectedSectionId(templateSectionsWithId[0].id);
    }
  };

  const sections = data.templateSections;

  const updateSection = useCallback(
    (sectionId: string, updates: Partial<TemplateSectionCreationData>) => {
      setData((currentData: TemplateCreationData) => {
        const currentSections = currentData.templateSections;
        const updatedSections = currentSections.map((section: TemplateSectionCreationData) =>
          section.id === sectionId ? { ...section, ...updates } : section,
        );

        const populatedMarkdownChanged = updatedSections.reduce(
          (acc: string, section: TemplateSectionCreationData) => {
            if (!section.sectionHeader) {
              acc +=
                '#'.repeat(section.sectionHeaderLevel) + (section.sectionHeader || '') + '\n\n';
            }
            return acc + section.populatedMarkdown + '\n\n';
          },
          '',
        );

        const templatedMarkdownChanged = updatedSections.reduce(
          (acc: string, section: TemplateSectionCreationData) => {
            if (!section.sectionHeader) {
              acc +=
                '#'.repeat(section.sectionHeaderLevel) + (section.sectionHeader || '') + '\n\n';
            }
            return acc + section.templatedMarkdown + '\n\n';
          },
          '',
        );

        return {
          ...currentData,
          templateSections: updatedSections,
          examplePopulatedMarkdown: populatedMarkdownChanged,
          exampleTemplateMarkdown: templatedMarkdownChanged,
        };
      });
    },
    [setData],
  );

  const toggleContentComplete = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) {
      return;
    }
    updateSection(sectionId, { isContentComplete: !section.isContentComplete });
  };

  const handleSectionLevelChange = (level: number) => {
    setData({
      ...data,
      sectionSplittingLevel: level as SectionSplittingLevel,
    });
  };

  const handleSectionReorder = (fromIndex: number, toIndex: number) => {
    const newSections = [...sections];
    const [movedSection] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, movedSection);

    setData({
      ...data,
      templateSections: newSections,
    });
  };

  const allContentCompleted = sections.every(s => s.isContentComplete);

  const { isLoading, progress, createTemplateStream } = useCreateTemplateStream();
  async function handleNextStep() {
    if (!allContentCompleted) {
      return;
    }

    try {
      const template = await createTemplateStream(data);
      const newData = {
        ...data,
        createdTemplate: template,
      };
      setData(newData);
      props.nextStep(newData);
    } catch (error) {
      notifyAndLogError(
        'Failed to create template',
        'Something went wrong when creating the template. Please try again and contact support is the problem persists',
        error,
        { templateData: data },
      );
    }
  }

  const handleSectionSplit = (sectionId: string, newSections: TemplateSectionCreationData[]) => {
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    if (sectionIndex === -1) return;

    const sectionsWithId = newSections.map(section => ({ ...section, id: uuidv4() }));

    const updatedSections = [
      ...sections.slice(0, sectionIndex),
      ...sectionsWithId,
      ...sections.slice(sectionIndex + 1),
    ];

    setData({
      ...data,
      templateSections: updatedSections,
    });

    if (sectionsWithId.length > 0) {
      setSelectedSectionId(sectionsWithId[0].id);
    }
  };

  const handleMergeSectionUp = (section: TemplateSectionCreationData) => {
    const sectionIndex = sections.findIndex(s => s.id === section.id);
    if (sectionIndex === 0) {
      return;
    }
    const mergedSection = mergeSectionsIntoOne([
      sections[sectionIndex - 1],
      sections[sectionIndex],
    ]);
    const updatedSections = [
      ...sections.slice(0, sectionIndex - 1),
      mergedSection,
      ...sections.slice(sectionIndex + 1),
    ];
    setData({
      ...data,
      templateSections: updatedSections,
    });
    setSelectedSectionId(mergedSection.id);
  };

  const handleDeleteSection = (sectionToDelete: TemplateSectionCreationData) => {
    const sectionIndex = sections.findIndex(s => s.id === sectionToDelete.id);
    const updatedSections = sections.filter(section => sectionToDelete.id !== section.id);
    setData({
      ...data,
      templateSections: updatedSections,
    });

    if (selectedSectionId === sectionToDelete.id) {
      const newSectionId =
        sectionIndex >= updatedSections.length
          ? updatedSections[sectionIndex - 1]?.id
          : updatedSections[sectionIndex + 1]?.id;
      setSelectedSectionId(newSectionId);
    }
  };

  const handleEditSectionHeader = (section: TemplateSectionCreationData, newHeader: string) => {
    updateSection(section.id, { sectionHeader: newHeader });
  };

  const handleSectionHeaderLevelChange = (
    section: TemplateSectionCreationData,
    newLevel: number,
  ) => {
    updateSection(section.id, { sectionHeaderLevel: newLevel });
  };

  const sectionsWithMissingPopulatedMarkdown = sections.filter(
    s => !s.isSectionInPopulatedMarkdown,
  );

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Edit section content"
      description="Review each section's content and split your document by the appropriate header level."
      continueText="Continue"
      canGoForward={allContentCompleted}
      hideProgress={true}
      nextStep={handleNextStep}
      isSubmitting={isLoading}
    >
      {isLoading && progress && (
        <Alert variant="light" color="blue.5" radius="lg" mb={'xxs'}>
          <Group gap="sm">
            <Loader size="sm" />
            <Text>{progress}</Text>
          </Group>
        </Alert>
      )}
      {sectionsWithMissingPopulatedMarkdown.length > 0 && (
        <Alert
          variant="light"
          color="red.5"
          radius="lg"
          icon={<IconBug color="var(--mantine-color-red-9)" />}
          mb={'xxs'}
        >
          <Text>The following template sections are missing an equivalent populated markdown</Text>
          <List>
            {sectionsWithMissingPopulatedMarkdown.map(s => (
              <List.Item key={s.id}>{s.sectionHeader}</List.Item>
            ))}
          </List>
        </Alert>
      )}
      <SectionEditorLayout
        onMergeSection={handleMergeSectionUp}
        onDeleteSection={handleDeleteSection}
        onEditSectionHeader={handleEditSectionHeader}
        sections={sections}
        selectedSectionId={selectedSectionId}
        onSectionSelect={setSelectedSectionId}
        showLevelSelector={true}
        sectionLevel={data.sectionSplittingLevel}
        onSectionLevelChange={handleSectionLevelChange}
        isSectionCompleted={section => section.isContentComplete}
        onToggleComplete={toggleContentComplete}
        onSectionReorder={handleSectionReorder}
        onSectionHeaderLevelChange={handleSectionHeaderLevelChange}
        renderContent={(selectedSection, onToggleComplete) => (
          <SectionContentEditorPane
            section={selectedSection}
            onUpdateSection={updateSection}
            onToggleComplete={onToggleComplete}
            isCompleted={selectedSection.isContentComplete}
            onSectionSplit={sections => handleSectionSplit(selectedSectionId!, sections)}
            placeholderImages={data.extractedImagesCollection?.images}
          />
        )}
      />
    </DrawerFlowScreenLayout>
  );
}
